import {SEASONS} from '@generic/data/assumptions';
import getMarketRateElements from './shared/getMarketRateElements';
import sharedConEdStaticRateElements from './shared/sharedConEdStaticRateElements';
import {
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER,
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER,
} from './constants';
import getSC9VDERRateElement from './shared/getSC9VDERRateElement';

// Service Classification ("SC") 9
// Rate 1
const RATE_NAME = 'sc9Rate1';

/**
 * MSC - Market Supply Charge
 * Calculated using ConEd's Market Supply Charge Calculator here:
 * https://www.coned.com/en/accounts-billing/your-bill/rate-calculators/market-supply-charge
 *
 * SC9 rate is equivalent to EL9 - General - Large Commercial Rate. Different name, same thing.
 * Rates I, III and IV are the same.
 * Rates II and V are the same.
 *
 * for EL9 I-V, for the dates Jan 1, 2024 - Dec 31, 2024 are shown as on peak/off peak/standard.
 * April 10, 2025:
 * 1. Do NOT use high/low tension. No impact on rates.
 * 2. Use the standard charge.
 * https://twist.com/a/9369/ch/691652/t/6861262/c/94867920
 */
const MSC_ENERGY_CHARGES = {
  H: 0.065397,
  I: 0.065798,
  J: 0.067563,
};

const FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS_2025 = 71.0;

// Page 656 - ./docs/coned-tariff-rate-book-2025.pdf
// PDF from - https://lite.coned.com/_external/cerates/documents/elecPSC10/electric-tariff.pdf
const DEMAND_DELIVERY_HIGH_TENSION_OTHER_DOLLARS_PER_KW_2025 = 20.51;
const DEMAND_DELIVERY_HIGH_TENSION_SUMMER_DOLLARS_PER_KW_2025 = 29.5;
const DEMAND_DELIVERY_LOW_TENSION_OTHER_DOLLARS_PER_KW_2025 = 33.5;
const DEMAND_DELIVERY_LOW_TENSION_SUMMER_DOLLARS_PER_KW_2025 = 42.8;

// Page 656 - ./docs/coned-tariff-rate-book-2025.pdf
// PDF from - https://lite.coned.com/_external/cerates/documents/elecPSC10/electric-tariff.pdf
const ENERGY_DELIVERY_HIGH_TENSION_CHARGE_DOLLARS_PER_KWH_2025 = 0.0154;
const ENERGY_DELIVERY_LOW_TENSION_CHARGE_DOLLARS_PER_KWH_2025 = 0.0166;

// Demand
// Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
// PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER = 2.84;
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER = 8.92;
const getMscCapacityCharge = ({isOnMarketBasedRate, isLocatedInWestchester}) => {
  if (isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER;
  }
  if (isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER;
  }
  return 0;
};

const sc9Rate1 = ({zone, isHighTension, isOnMarketBasedRate, isLocatedInWestchester}) => ({
  name: RATE_NAME,
  title: 'SC 9 Rate I',
  maxKw: 1500,
  rateElements: [
    ...sharedConEdStaticRateElements,
    ...getMarketRateElements(zone, isOnMarketBasedRate),
    ...getSC9VDERRateElement({isHighTension, isLocatedInWestchester, isOnMarketBasedRate})
      .NonStandby,
    {
      rateElementType: 'MonthlyDemand',
      name: 'MSC Capacity Charge',
      billingCategory: 'supply',
      rateComponents: [
        // Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
        // PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
        {
          charge: getMscCapacityCharge({isOnMarketBasedRate, isLocatedInWestchester}),
          name: 'MSC Capacity Charge',
        },
      ],
    },
    {
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Energy Charge',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isOnMarketBasedRate ? 0 : MSC_ENERGY_CHARGES[zone],
          name: 'MSC Energy Charge',
        },
      ],
    },
    {
      rateElementType: 'FixedPerMonth',
      name: 'Customer Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS_2025,
          name: 'Customer Charge',
        },
      ],
    },
    {
      rateElementType: 'MonthlyEnergy',
      name: 'Energy Delivery Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: isHighTension
            ? ENERGY_DELIVERY_HIGH_TENSION_CHARGE_DOLLARS_PER_KWH_2025
            : ENERGY_DELIVERY_LOW_TENSION_CHARGE_DOLLARS_PER_KWH_2025,
          name: 'Energy Delivery Charge',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_dlm.asp
    // ./docs/april-2025-rate-update/june-2024-dynamic-load-management-surcharge.pdf
    {
      rateElementType: 'MonthlyDemand',
      name: 'Dynamic Load Management ("DLM") Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0.29,
          name: 'CSRP & Term-/Auto-DLM Component',
        },
        {
          charge: 0.17,
          name: 'DLC & DLRP Component',
        },
      ],
    },
    {
      rateElementType: 'MonthlyDemand',
      name: 'Demand Delivery Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          // This rate, like all SC9 rates has an "expected minimum" of 10kW of demand.
          // We are explicitly not "testing eligibility" for this rate,
          // so we just consider this rate "MontlyDemand".
          // Discussed in meeting on Apr. 8, 2025 - https://drive.google.com/file/d/1RGiu5PsG4KlTrS9jyTA8xtD32gufXkNo/view
          // Page 656 - ./docs/coned-tariff-rate-book-2025.pdf
          charge: isHighTension
            ? SEASONS.map((season) =>
                season === 'SUMMER'
                  ? DEMAND_DELIVERY_HIGH_TENSION_SUMMER_DOLLARS_PER_KW_2025
                  : DEMAND_DELIVERY_HIGH_TENSION_OTHER_DOLLARS_PER_KW_2025
              )
            : SEASONS.map((season) =>
                season === 'SUMMER'
                  ? DEMAND_DELIVERY_LOW_TENSION_SUMMER_DOLLARS_PER_KW_2025
                  : DEMAND_DELIVERY_LOW_TENSION_OTHER_DOLLARS_PER_KW_2025
              ),
          name: 'Demand Delivery Charge',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_sdr.asp
    // ./docs/april-2025-rate-update/march-2025-delivery-revenue-surcharge.pdf
    {
      id: 'deliveryRevenueSurcharge',
      rateElementType: 'MonthlyEnergy',
      name: 'Delivery Revenue Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0.0001,
          name: 'Delivery Revenue Surcharge',
        },
      ],
    },
    // ./docs/april-2025-rate-update/msc-adjustment-factor.pdf
    // PDF from - https://lite.coned.com/_external/cerates/PSC10-msc-adjustments.asp
    // Non-residential Use
    {
      id: 'mscAdjustmentFactor',
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Adjustment Factor',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isLocatedInWestchester ? 0.003734 : -0.004456,
          name: 'MSC Adjustment Factor',
        },
      ],
    },
  ],
});

export default sc9Rate1;
